import { Directive, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ConfirmationService, FormChangeTracker } from '../../../services/confirmation.service';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { Router } from '@angular/router';

/**
 * Clase base para formularios con confirmaciones estandarizadas
 * Aplica el principio DRY (Don't Repeat Yourself) y Template Method Pattern
 */
@Directive()
export abstract class BaseFormComponent implements OnInit, OnDestroy, FormChangeTracker {
  
  @ViewChild('confirmationModal') confirmationModal!: ConfirmationModalComponent;
  
  // Estado del formulario
  protected initialFormValue: any = {};
  protected formSubscriptions: Subscription[] = [];
  
  // Modal de confirmación
  showConfirmationModal = false;
  confirmationConfig: any = {};
  private confirmationResolve: ((result: boolean) => void) | null = null;

  constructor(
    protected confirmationService: ConfirmationService,
    protected router: Router
  ) {
    // Suscribirse a las confirmaciones del servicio
    this.confirmationService.confirmation$.subscribe(({ config, resolve }) => {
      this.confirmationConfig = config;
      this.confirmationResolve = resolve;
      this.showConfirmationModal = true;
    });
  }

  ngOnInit(): void {
    this.initializeForm();
    this.setupFormTracking();
    this.setupCustomValidations();
  }

  ngOnDestroy(): void {
    this.formSubscriptions.forEach(sub => sub.unsubscribe());
  }

  // === MÉTODOS ABSTRACTOS (Template Method Pattern) ===
  
  /**
   * Inicializar el formulario específico - debe ser implementado por cada componente
   */
  protected abstract initializeForm(): void;

  /**
   * Obtener el FormGroup principal - debe ser implementado por cada componente
   */
  protected abstract getMainForm(): FormGroup;

  /**
   * Obtener el nombre del formulario para mostrar en confirmaciones
   */
  public abstract getFormName(): string;

  /**
   * Obtener datos críticos para mostrar en confirmación de envío
   */
  public getCriticalData?(): string[];

  /**
   * Configurar validaciones personalizadas del formulario
   */
  protected setupCustomValidations(): void {
    // Implementación por defecto vacía - los componentes pueden sobrescribirla
  }

  // === IMPLEMENTACIÓN DE FormChangeTracker ===

  /**
   * Verifica si hay cambios no guardados en el formulario
   */
  hasUnsavedChanges(): boolean {
    const currentForm = this.getMainForm();
    if (!currentForm || !this.initialFormValue) {
      return false;
    }

    return JSON.stringify(this.initialFormValue) !== JSON.stringify(currentForm.value);
  }

  // === MÉTODOS DE SEGUIMIENTO DE CAMBIOS ===

  /**
   * Configura el seguimiento de cambios en el formulario
   */
  protected setupFormTracking(): void {
    const form = this.getMainForm();
    if (form) {
      // Guardar valor inicial del formulario
      this.initialFormValue = { ...form.value };
      
      // Escuchar cambios en el formulario
      const changesSub = form.valueChanges.subscribe(() => {
        this.onFormValueChange();
      });
      
      this.formSubscriptions.push(changesSub);
    }
  }

  /**
   * Método llamado cuando el formulario cambia - puede ser sobrescrito
   */
  protected onFormValueChange(): void {
    // Implementación por defecto vacía
  }

  /**
   * Resetea el seguimiento de cambios (útil después de guardar)
   */
  protected resetChangeTracking(): void {
    const form = this.getMainForm();
    if (form) {
      this.initialFormValue = { ...form.value };
    }
  }

  // === MÉTODOS DE CONFIRMACIÓN ===

  /**
   * Maneja la confirmación del modal
   */
  onModalConfirmed(): void {
    console.log('[BaseFormComponent] Modal confirmed, resolving promise with true');
    if (this.confirmationResolve) {
      // Resolver la promesa PRIMERO, antes de ocultar el modal
      const resolve = this.confirmationResolve;
      this.confirmationResolve = null;
      // Resolver inmediatamente
      resolve(true);
      // Ocultar el modal después de resolver
      this.showConfirmationModal = false;
    } else {
      console.warn('[BaseFormComponent] No confirmationResolve function found!');
      this.showConfirmationModal = false;
    }
  }

  /**
   * Maneja la cancelación del modal
   */
  onModalCancelled(): void {
    console.log('[BaseFormComponent] Modal cancelled, resolving promise with false');
    if (this.confirmationResolve) {
      const resolve = this.confirmationResolve;
      this.confirmationResolve = null;
      resolve(false);
      this.showConfirmationModal = false;
    } else {
      this.showConfirmationModal = false;
    }
  }

  /**
   * Maneja el cierre del modal
   */
  onModalClosed(): void {
    console.log('[BaseFormComponent] Modal closed, resolving promise with false');
    if (this.confirmationResolve) {
      const resolve = this.confirmationResolve;
      this.confirmationResolve = null;
      resolve(false);
      this.showConfirmationModal = false;
    } else {
      this.showConfirmationModal = false;
    }
  }

  // === MÉTODOS DE ACCIONES COMUNES ===

  /**
   * Maneja la cancelación del formulario con confirmación
   */
  async handleFormCancel(redirectRoute?: string): Promise<void> {
    const canCancel = await this.confirmationService.confirmFormCancel(this);
    
    if (canCancel) {
      if (redirectRoute) {
        this.router.navigate([redirectRoute]);
      } else {
        this.navigateBack();
      }
    }
  }

  /**
   * Maneja el envío del formulario con confirmación
   */
  async handleFormSubmit(): Promise<void> {
    console.log('[BaseFormComponent] handleFormSubmit called');
    const form = this.getMainForm();
    
    if (!form.valid) {
      console.log('[BaseFormComponent] Form is invalid, marking as touched');
      this.markFormGroupTouched(form);
      return;
    }

    console.log('[BaseFormComponent] Requesting confirmation...');
    const canSubmit = await this.confirmationService.confirmFormSubmit(this);
    console.log('[BaseFormComponent] Confirmation result:', canSubmit);
    
    if (canSubmit) {
      console.log('[BaseFormComponent] Confirmation accepted, calling performFormSubmit');
      await this.performFormSubmit();
      this.resetChangeTracking(); // Resetear después de guardar exitosamente
    } else {
      console.log('[BaseFormComponent] Confirmation rejected, not submitting');
    }
  }

  /**
   * Maneja el reset del formulario con confirmación
   */
  async handleFormReset(): Promise<void> {
    const canReset = await this.confirmationService.confirmFormReset(this.getFormName());
    
    if (canReset) {
      this.performFormReset();
      this.resetChangeTracking();
    }
  }

  /**
   * Confirma eliminación de elementos
   */
  async confirmDelete(itemName: string, itemDescription?: string): Promise<boolean> {
    return await this.confirmationService.confirmDelete(itemName, itemDescription);
  }

  /**
   * Confirma cambios que causan pérdida de datos
   */
  async confirmDataLoss(changeDescription: string): Promise<boolean> {
    return await this.confirmationService.confirmDataLoss(changeDescription);
  }

  // === MÉTODOS ABSTRACTOS PARA IMPLEMENTAR ===

  /**
   * Realizar el envío real del formulario - debe ser implementado
   */
  protected abstract performFormSubmit(): Promise<void>;

  /**
   * Realizar el reset del formulario - implementación por defecto
   */
  protected performFormReset(): void {
    const form = this.getMainForm();
    if (form) {
      form.reset();
      this.initialFormValue = { ...form.value };
    }
  }

  /**
   * Navegar hacia atrás - implementación por defecto
   */
  protected navigateBack(): void {
    window.history.back();
  }

  // === MÉTODOS UTILITARIOS ===

  /**
   * Marca todos los campos del formulario como touched para mostrar errores
   */
  protected markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control) {
        control.markAsTouched();
        
        if (control instanceof FormGroup) {
          this.markFormGroupTouched(control);
        }
      }
    });
  }

  /**
   * Verifica si un campo específico tiene errores y ha sido tocado
   */
  hasFieldError(fieldName: string, errorType?: string): boolean {
    const form = this.getMainForm();
    const field = form.get(fieldName);
    
    if (!field) return false;
    
    if (errorType) {
      return field.hasError(errorType) && field.touched;
    }
    
    return field.invalid && field.touched;
  }

  /**
   * Obtiene el mensaje de error para un campo específico
   */
  getFieldErrorMessage(fieldName: string): string {
    const form = this.getMainForm();
    const field = form.get(fieldName);
    
    if (!field || !field.errors) return '';
    
    const errors = field.errors;
    
    if (errors['required']) return `${fieldName} es requerido`;
    if (errors['email']) return 'Email inválido';
    if (errors['min']) return `Valor mínimo: ${errors['min'].min}`;
    if (errors['max']) return `Valor máximo: ${errors['max'].max}`;
    if (errors['minlength']) return `Mínimo ${errors['minlength'].requiredLength} caracteres`;
    if (errors['maxlength']) return `Máximo ${errors['maxlength'].requiredLength} caracteres`;
    
    return 'Campo inválido';
  }
}
